import { useState } from "react";
import { Download, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMspBilling } from "@/hooks/use-api";
import {
  billingCsv,
  formatBytes,
  formatPeriod,
  recentPeriods,
} from "@/lib/msp-billing";
import type { MspTenant } from "@/types/api";

interface MspBillingSummaryProps {
  tenants: MspTenant[];
}

// Derived once at module load, not per render — a session left open across a
// month boundary would otherwise shift the list under the user's selection.
const PERIODS = recentPeriods(new Date());

export function MspBillingSummary({ tenants }: MspBillingSummaryProps) {
  const [period, setPeriod] = useState(PERIODS[0]);
  const { data, isLoading, isError } = useMspBilling(period);

  const children = data?.children ?? [];
  const totals = data?.billing_summary;
  const planById = new Map(tenants.map((t) => [t.id, t.plan]));

  function exportCsv() {
    const url = URL.createObjectURL(
      new Blob([billingCsv(children, tenants, period)], { type: "text/csv" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `client-usage-${period}.csv`;
    a.click();
    // Safari and older Firefox have not started fetching the blob when click()
    // returns; revoking synchronously yields an empty download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-3">
          <Receipt className="h-5 w-5 text-primary" />
          <div>
            <CardTitle>Client usage</CardTitle>
            <p className="text-sm text-muted-foreground">
              Per-client consumption for your own invoicing
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label="Billing period"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {formatPeriod(p)}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={children.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Usage could not be loaded. Try again shortly.
          </p>
        ) : children.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No usage recorded for {formatPeriod(period)}.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Endpoints</TableHead>
                <TableHead className="text-right">Events</TableHead>
                <TableHead className="text-right">Alerts</TableHead>
                <TableHead className="text-right">Storage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {children.map((c) => (
                <TableRow key={c.tenant_id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {planById.get(c.tenant_id) ?? "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.endpoints.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.events.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.alerts.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatBytes(c.storage_bytes)}
                  </TableCell>
                </TableRow>
              ))}
              {totals && (
                <TableRow className="border-t-2 font-medium">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-muted-foreground">
                    {totals.total_children} clients
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {totals.total_endpoints.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {totals.total_events.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {totals.total_alerts.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatBytes(totals.total_storage_bytes)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
